import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import SelectDropdown from '../components/SelectDropdown';
import '../styles/pages/_consultant-wellness.scss';

interface WellnessItem {
  id: string;
  title: string;
  type: 'module' | 'session';
  category: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  status: 'draft' | 'published';
}

const ConsultantWellnessPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [items, setItems] = useState<WellnessItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'module' | 'session'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'draft' | 'published'>('all');

  // Fetch modules and sessions for current consultant
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const TOKEN = token || localStorage.getItem('token');
        if (!TOKEN) {
          navigate('/login');
          return;
        }

        if (!user?.id) {
          toast.error('User information not available');
          setLoading(false);
          return;
        }

        // Fetch modules and sessions created by this consultant
        const [modulesRes, sessionsRes] = await Promise.all([
          fetch(`/api/wellness-modules/consultant/${user.id}`, {
            headers: {
              'Authorization': `Bearer ${TOKEN}`
            }
          }),
          fetch(`/api/wellness-sessions/consultant/${user.id}`, {
            headers: {
              'Authorization': `Bearer ${TOKEN}`
            }
          })
        ]);

        const modulesData = modulesRes.ok ? await modulesRes.json() : { data: { modules: [] } };
        const sessionsData = sessionsRes.ok ? await sessionsRes.json() : { data: { sessions: [] } };

        const modules = modulesData.data?.modules || modulesData.modules || [];
        const sessions = sessionsData.data?.sessions || sessionsData.sessions || [];

        const combinedItems: WellnessItem[] = [
          ...modules.map((m: any) => ({
            id: m._id || m.id,
            title: m.title,
            type: 'module' as const,
            category: m.category,
            description: m.description,
            createdAt: m.createdAt,
            updatedAt: m.updatedAt,
            status: m.status
          })),
          ...sessions.map((s: any) => ({
            id: s._id || s.id,
            title: s.title,
            type: 'session' as const,
            category: s.category,
            description: s.description,
            createdAt: s.createdAt,
            updatedAt: s.updatedAt,
            status: s.status
          }))
        ];

        setItems(combinedItems);
      } catch (error) {
        console.error('Error fetching wellness data:', error);
        toast.error('Failed to load wellness modules and sessions');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, token, user?.id]);

  // Filter and search items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesType = filterType === 'all' || item.type === filterType;
      const matchesStatus = filterStatus === 'all' || item.status === filterStatus;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [items, searchQuery, filterType, filterStatus]);

  // Group items by type
  const groupedItems = useMemo(() => {
    return {
      modules: filteredItems.filter(item => item.type === 'module'),
      sessions: filteredItems.filter(item => item.type === 'session')
    };
  }, [filteredItems]);

  const handleCreateModule = () => {
    navigate('/panel/consultant/wellness-module-creation');
  };

  const handleCreateSession = () => {
    navigate('/panel/consultant/wellness-session-creation');
  };

  const handleEditItem = (itemId: string, itemType: string) => {
    if (itemType === 'module') {
      navigate(`/panel/consultant/wellness-module-creation/${itemId}`);
    } else {
      navigate(`/panel/consultant/wellness-session-creation/${itemId}`);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        // TODO: Call delete API when available
        setItems(items.filter(item => item.id !== itemId));
        toast.success('Item deleted successfully');
      } catch (error) {
        toast.error('Failed to delete item');
      }
    }
  };

  const renderItemCard = (item: WellnessItem) => (
    <div key={item.id} className={`wellness-card wellness-card--${item.type}`}>
      <div className="wellness-card__header">
        <div className="wellness-card__type-badge">
          <span className="material-symbols-outlined wellness-card__type-icon">
            {item.type === 'module' ? 'layers' : 'play_circle'}
          </span>
          {item.type === 'module' ? 'Module' : 'Session'}
        </div>
        <span className={`wellness-card__status wellness-card__status--${item.status}`}>
          {item.status === 'draft' ? 'Draft' : 'Published'}
        </span>
      </div>

      <div className="wellness-card__title">{item.title}</div>
      <div className="wellness-card__category">{item.category}</div>
      <div className="wellness-card__description">{item.description}</div>

      <div className="wellness-card__meta">
        <span className="wellness-card__date">
          Updated: {new Date(item.updatedAt).toLocaleDateString()}
        </span>
      </div>

      <div className="wellness-card__actions">
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => handleEditItem(item.id, item.type)}
        >
          <span className="material-symbols-outlined">edit</span>
          Edit
        </button>
        <button
          className="btn btn-danger btn-sm"
          onClick={() => handleDeleteItem(item.id)}
        >
          <span className="material-symbols-outlined">delete</span>
          Delete
        </button>
      </div>
    </div>
  );

  return (
    <div className={`consultant-wellness ${isDarkMode ? 'dark' : ''}`}>
      <div className="consultant-wellness__container">
        {/* Header */}
        <div className="consultant-wellness__header">
          <div className="consultant-wellness__title-section">
            <h1 className="consultant-wellness__title">Wellness Creation Center</h1>
            <p className="consultant-wellness__subtitle">Create and manage wellness modules and sessions</p>
          </div>

          <div className="consultant-wellness__header-actions">
            <button
              className="btn btn-primary btn-lg"
              onClick={handleCreateModule}
            >
              <span className="material-symbols-outlined">add</span>
              Create Module
            </button>
            <button
              className="btn btn-primary btn-lg"
              onClick={handleCreateSession}
            >
              <span className="material-symbols-outlined">add</span>
              Create Session
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="consultant-wellness__controls">
          <div className="consultant-wellness__search-bar">
            <span className="material-symbols-outlined consultant-wellness__search-icon">search</span>
            <input
              type="text"
              className="consultant-wellness__search-input"
              placeholder="Search by title or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="consultant-wellness__filters">
            <SelectDropdown
              value={filterType}
              onChange={(value) => setFilterType(value as any)}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'module', label: 'Modules' },
                { value: 'session', label: 'Sessions' }
              ]}
              placeholder="Filter by type"
              ariaLabel="Filter by type"
            />

            <SelectDropdown
              value={filterStatus}
              onChange={(value) => setFilterStatus(value as any)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'draft', label: 'Draft' },
                { value: 'published', label: 'Published' }
              ]}
              placeholder="Filter by status"
              ariaLabel="Filter by status"
            />
          </div>
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="consultant-wellness__loading">
            <div className="consultant-wellness__loading-spinner"></div>
            <p>Loading wellness items...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="consultant-wellness__empty-state">
            <div className="consultant-wellness__empty-icon">📋</div>
            <h3>No wellness items found</h3>
            <p>
              {items.length === 0
                ? 'Start creating wellness modules and sessions to get started.'
                : 'No items match your search or filters.'}
            </p>
            {items.length === 0 && (
              <div className="consultant-wellness__empty-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleCreateModule}
                >
                  Create Your First Module
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleCreateSession}
                >
                  Create Your First Session
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="consultant-wellness__content">
            {/* Modules Section */}
            {groupedItems.modules.length > 0 && (
              <section className="consultant-wellness__section">
                <h2 className="consultant-wellness__section-title">
                  <span className="material-symbols-outlined">layers</span>
                  Wellness Modules ({groupedItems.modules.length})
                </h2>
                <div className="consultant-wellness__grid">
                  {groupedItems.modules.map(renderItemCard)}
                </div>
              </section>
            )}

            {/* Sessions Section */}
            {groupedItems.sessions.length > 0 && (
              <section className="consultant-wellness__section">
                <h2 className="consultant-wellness__section-title">
                  <span className="material-symbols-outlined">play_circle</span>
                  Wellness Sessions ({groupedItems.sessions.length})
                </h2>
                <div className="consultant-wellness__grid">
                  {groupedItems.sessions.map(renderItemCard)}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsultantWellnessPage;
